// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from './AuthContext';

const CartContext = createContext({});

export function CartProvider({ children }) {
  const { customer } = useAuth();
  const [cart, setCart]         = useState(null);     // cart record
  const [items, setItems]       = useState([]);        // cart items with product data
  const [coupon, setCoupon]     = useState(null);
  const [fulfillment, setFulfillment] = useState('delivery');
  const [loading, setLoading]   = useState(false);
  const [isOpen, setIsOpen]     = useState(false);     // cart drawer open

  useEffect(() => {
    if (customer) loadCart();
    else { setCart(null); setItems([]); setCoupon(null); }
  }, [customer]);

  const loadCart = useCallback(async () => {
    setLoading(true);
    try {
      // Find active cart
      let { data: existingCart } = await supabase
        .from('grocery_carts')
        .select('*, grocery_coupons(*)')
        .eq('customer_id', customer.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!existingCart) {
        const { data: newCart } = await supabase
          .from('grocery_carts')
          .insert({ customer_id: customer.id, status: 'active', fulfillment })
          .select('*, grocery_coupons(*)')
          .single();
        existingCart = newCart;
      }

      setCart(existingCart);
      setCoupon(existingCart?.grocery_coupons || null);

      // Load items
      const { data: cartItems } = await supabase
        .from('grocery_cart_items')
        .select('*, grocery_products(*)')
        .eq('cart_id', existingCart.id);
      setItems(cartItems || []);
    } catch (err) {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [customer]);

  const addItem = async (product, quantity = 1) => {
    if (!cart) return;
    const price = product.sale_price_cents || product.price_cents;

    // Check if already in cart
    const existing = items.find(i => i.product_id === product.id);
    if (existing) {
      const { data } = await supabase
        .from('grocery_cart_items')
        .update({ quantity: existing.quantity + quantity, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select('*, grocery_products(*)')
        .single();
      setItems(prev => prev.map(i => i.id === existing.id ? data : i));
    } else {
      const { data } = await supabase
        .from('grocery_cart_items')
        .insert({ cart_id: cart.id, product_id: product.id, quantity, unit_price_cents: price })
        .select('*, grocery_products(*)')
        .single();
      setItems(prev => [...prev, data]);
    }
    setIsOpen(true);
  };

  const removeItem = async (itemId) => {
    await supabase.from('grocery_cart_items').delete().eq('id', itemId);
    setItems(prev => prev.filter(i => i.id !== itemId));
  };

  const updateQuantity = async (itemId, quantity) => {
    if (quantity <= 0) { removeItem(itemId); return; }
    const { data } = await supabase
      .from('grocery_cart_items')
      .update({ quantity, updated_at: new Date().toISOString() })
      .eq('id', itemId)
      .select('*, grocery_products(*)')
      .single();
    setItems(prev => prev.map(i => i.id === itemId ? data : i));
  };

  const applyCoupon = async (code) => {
    const { data: couponData, error } = await supabase
      .from('grocery_coupons')
      .select('*')
      .eq('code', code.toUpperCase())
      .eq('active', true)
      .single();

    if (error || !couponData) throw new Error('Invalid or expired coupon code.');
    if (couponData.valid_until && new Date(couponData.valid_until) < new Date())
      throw new Error('This coupon has expired.');
    if (couponData.max_uses && couponData.uses_count >= couponData.max_uses)
      throw new Error('This coupon has reached its usage limit.');

    await supabase.from('grocery_carts').update({ coupon_id: couponData.id }).eq('id', cart.id);
    setCoupon(couponData);
    return couponData;
  };

  const removeCoupon = async () => {
    await supabase.from('grocery_carts').update({ coupon_id: null }).eq('id', cart.id);
    setCoupon(null);
  };

  const clearCart = async () => {
    if (!cart) return;
    await supabase.from('grocery_cart_items').delete().eq('cart_id', cart.id);
    await supabase.from('grocery_carts').update({ status: 'converted' }).eq('id', cart.id);
    setItems([]);
    setCart(null);
    setCoupon(null);
  };

  // Computed values
  const subtotal = items.reduce((sum, i) => sum + (i.unit_price_cents * i.quantity), 0);

  const discount = (() => {
    if (!coupon) return 0;
    if (coupon.discount_type === 'percent') return Math.round(subtotal * (coupon.discount_value / 100));
    if (coupon.discount_type === 'fixed')   return Math.min(coupon.discount_value * 100, subtotal);
    if (coupon.discount_type === 'free_delivery') return 0;
    return 0;
  })();

  const deliveryFee = (() => {
    if (fulfillment === 'pickup') return 0;
    if (coupon?.discount_type === 'free_delivery') return 0;
    return subtotal >= 7500 ? 0 : 499; // Free delivery over $75
  })();

  const tax     = Math.round((subtotal - discount) * 0.07); // 7% tax
  const total   = subtotal - discount + deliveryFee + tax;
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart, items, coupon, fulfillment, setFulfillment,
      loading, isOpen, setIsOpen,
      addItem, removeItem, updateQuantity, applyCoupon, removeCoupon, clearCart, loadCart,
      subtotal, discount, deliveryFee, tax, total, itemCount,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
